/**
 * Production-Safe Structured Logging System
 *
 * Replaces 1,711 console.log statements across the codebase with:
 * - Environment-aware logging (silent in production)
 * - Structured log levels (debug, info, warn, error)
 * - Performance measurement
 * - Error tracking integration ready
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  component?: string;
  action?: string;
  userId?: string;
  profileId?: string;
  [key: string]: any;
}

class Logger {
  private isDevelopment = import.meta.env.DEV;
  private isProduction = import.meta.env.PROD;

  /**
   * Debug-level logs (development only)
   * Use for: Detailed debugging, variable dumps
   */
  debug(message: string, context?: LogContext) {
    if (!this.isDevelopment) return;
    this.log('debug', message, context);
  }

  /**
   * Info-level logs (development only)
   * Use for: General application flow, successful operations
   */
  info(message: string, context?: LogContext) {
    if (!this.isDevelopment) return;
    this.log('info', message, context);
  }

  /**
   * Warning-level logs (development + production)
   * Use for: Recoverable errors, degraded functionality
   */
  warn(message: string, context?: LogContext) {
    this.log('warn', message, context);

    // In production, send to monitoring service
    if (this.isProduction) {
      this.sendToMonitoring('warn', message, context);
    }
  }

  /**
   * Error-level logs (always logged)
   * Use for: Unrecoverable errors, exceptions
   */
  error(message: string, error?: Error, context?: LogContext) {
    const errorContext = {
      ...context,
      errorMessage: error?.message,
      errorStack: error?.stack,
    };

    this.log('error', message, errorContext);

    // In production, send to error tracking service
    if (this.isProduction) {
      this.sendToMonitoring('error', message, errorContext);
    }
  }

  /**
   * Performance measurement
   * Use for: Tracking operation duration
   */
  time(label: string) {
    if (!this.isDevelopment) return;
    console.time(label);
  }

  timeEnd(label: string) {
    if (!this.isDevelopment) return;
    console.timeEnd(label);
  }

  /**
   * Measure async operation performance
   */
  async measure<T>(
    label: string,
    operation: () => Promise<T>
  ): Promise<T> {
    if (!this.isDevelopment) {
      return operation();
    }

    const start = performance.now();
    try {
      const result = await operation();
      const duration = performance.now() - start;
      this.info(`⏱️ ${label} completed in ${duration.toFixed(2)}ms`);
      return result;
    } catch (error) {
      const duration = performance.now() - start;
      this.error(`⏱️ ${label} failed after ${duration.toFixed(2)}ms`, error as Error);
      throw error;
    }
  }

  /**
   * Internal logging method with formatted output
   */
  private log(level: LogLevel, message: string, context?: LogContext) {
    const emoji = this.getEmoji(level);
    const timestamp = new Date().toISOString();

    const formattedMessage = context
      ? `${emoji} ${message} ${JSON.stringify(context)}`
      : `${emoji} ${message}`;

    switch (level) {
      case 'debug':
        console.debug(`[${timestamp}] ${formattedMessage}`);
        break;
      case 'info':
        console.info(`[${timestamp}] ${formattedMessage}`);
        break;
      case 'warn':
        console.warn(`[${timestamp}] ${formattedMessage}`);
        break;
      case 'error':
        console.error(`[${timestamp}] ${formattedMessage}`);
        break;
    }
  }

  /**
   * Get emoji indicator for log level
   */
  private getEmoji(level: LogLevel): string {
    switch (level) {
      case 'debug': return '🔍';
      case 'info': return '✅';
      case 'warn': return '⚠️';
      case 'error': return '❌';
    }
  }

  /**
   * Send logs to monitoring service (production)
   * Integrated with Sentry for error tracking and breadcrumbs
   */
  private sendToMonitoring(level: LogLevel, message: string, context?: LogContext) {
    // Sentry integration (only if Sentry is available)
    if (typeof window !== 'undefined' && (window as any).Sentry) {
      const Sentry = (window as any).Sentry;

      // Add breadcrumb for all levels
      Sentry.addBreadcrumb({
        type: level === 'error' ? 'error' : 'default',
        level: level,
        message: message,
        data: context,
        timestamp: Date.now() / 1000,
      });

      // Capture errors and warnings as Sentry events
      if (level === 'error') {
        Sentry.captureMessage(message, {
          level: 'error',
          extra: context,
        });
      } else if (level === 'warn') {
        Sentry.captureMessage(message, {
          level: 'warning',
          extra: context,
        });
      }
    }

    // Also store in session for admin debugging
    try {
      const logs = JSON.parse(sessionStorage.getItem('prod_logs') || '[]');
      logs.push({
        timestamp: new Date().toISOString(),
        level,
        message,
        context,
      });

      // Keep last 100 logs
      if (logs.length > 100) {
        logs.shift();
      }

      sessionStorage.setItem('prod_logs', JSON.stringify(logs));
    } catch {
      // Silent failure - don't break app if storage fails
    }
  }
}

// Export singleton instance
export const logger = new Logger();

// ============================================================================
// MIGRATION GUIDE: Replace console.log with logger
// ============================================================================
/*
// Before (1,711 instances like this):
logger.debug('🔍 Fetching profile matches for:', { profileId });
logger.debug('Profile search query:', {  query, sectors });
logger.error('Error fetching profile:', error);

// After:
import { logger } from '@/utils/logger';

logger.debug('Fetching profile matches', { profileId });
logger.info('Profile search query', { query, sectors });
logger.error('Error fetching profile', error, { profileId });

// Performance measurement:
// Before:
console.time('v4-smart-search');
const result = await searchWithSmartStrategy(query, 50);
console.timeEnd('v4-smart-search');

// After:
const result = await logger.measure('v4-smart-search', async () => {
  return searchWithSmartStrategy(query, 50);
});
*/
